import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { IonCheckbox, IonInput, IonItem, ToastController } from '@ionic/angular';
import { finalize } from 'rxjs';

import { GroupManagementService } from '@services/group-management.service';
import { PanelHostService } from '@services/panel-host.service';
import { TripService } from '@services/trip.service';
import { hasTripLeadership } from '../../../services/trip-access/trip-guards';
import { ContentContainerComponent } from '../../ui/content-container/content-container.component';
import { PanelBodyComponent } from '../../ui/panel-body/panel-body.component';

interface QuickAddFormValue {
  firstname: string;
  lastname: string;
  email: string;
  rolelead: boolean;
  rolefin: boolean;
}

@Component({
  selector: 'app-add-non-roster-user',
  imports: [
    ReactiveFormsModule,
    TranslocoDirective,
    IonInput,
    IonItem,
    IonCheckbox,
    ContentContainerComponent,
    PanelBodyComponent,
  ],
  templateUrl: './add-non-roster-user.component.html',
  styleUrls: ['./add-non-roster-user.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddNonRosterUserComponent {
  private readonly panelHost = inject(PanelHostService);
  private readonly tripService = inject(TripService);
  private readonly groupManagementService = inject(GroupManagementService);
  private readonly transloco = inject(TranslocoService);
  private readonly toastController = inject(ToastController);
  private readonly fb = inject(FormBuilder);

  readonly mode = computed(() => this.panelHost.addNonRosterUserMode());

  readonly quickAddForm: FormGroup = this.fb.group({
    firstname: ['', Validators.required],
    lastname: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    rolelead: [false],
    rolefin: [false],
  });

  submitting = signal<boolean>(false);
  duplicateEmailError = signal<boolean>(false);
  permissionsRequiredError = signal<boolean>(false);

  constructor() {
    effect(() => {
      if (this.panelHost.activePanel() === 'add-non-roster-user') {
        this.resetForm();
      }
    });
  }

  cancel(): void {
    this.panelHost.dismiss();
  }

  submit(): void {
    this.duplicateEmailError.set(false);
    this.permissionsRequiredError.set(false);

    if (this.mode() === 'permissions' && !this.permissionsSelected()) {
      this.permissionsRequiredError.set(true);
      return;
    }

    if (this.quickAddForm.invalid) {
      this.quickAddForm.markAllAsTouched();
      return;
    }

    const trip = this.tripService.currentTrip.value();
    if (!trip) {
      return;
    }

    const formValue = this.quickAddForm.getRawValue() as QuickAddFormValue;
    const rolelead = formValue.rolelead ? 1 : 0;
    const rolefin = formValue.rolefin ? 1 : 0;

    const participant = {
      firstname: formValue.firstname.trim(),
      lastname: formValue.lastname.trim(),
      email: formValue.email.trim(),
      ...(this.mode() === 'permissions' ? { rolelead, rolefin, roledrive: 0 } : {}),
    };

    if (this.isDuplicateEmail(trip, participant.email)) {
      this.duplicateEmailError.set(true);
      return;
    }

    this.submitting.set(true);
    const username = `${participant.firstname} ${participant.lastname}`;

    this.groupManagementService
      .addNonRosterUser(trip.id, participant)
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: async () => {
          await this.showSuccessToast(username, marker('group-management.add-non-roster-user.add-success'));
          this.panelHost.dismiss();
        },
        error: async () => {
          await this.showErrorToast(username, marker('group-management.add-non-roster-user.add-failed'));
        },
      });
  }

  emailErrorText(): string {
    const control = this.quickAddForm.get('email');
    if (control?.hasError('required')) {
      return this.transloco.translate(marker('group-management.add-non-roster-user.missing-fields'));
    }
    return this.transloco.translate(marker('group-management.add-non-roster-user.invalid-email'));
  }

  private permissionsSelected(): boolean {
    const formValue = this.quickAddForm.getRawValue() as QuickAddFormValue;
    return formValue.rolelead || formValue.rolefin;
  }

  private async showSuccessToast(username: string, messageKey: ReturnType<typeof marker>): Promise<void> {
    const toast = await this.toastController.create({
      message: this.transloco.translate(messageKey, { username }),
      duration: 3000,
      color: 'success',
    });
    await toast.present();
  }

  private async showErrorToast(username: string, messageKey: ReturnType<typeof marker>): Promise<void> {
    const toast = await this.toastController.create({
      message: this.transloco.translate(messageKey, { username }),
      duration: 4000,
      color: 'danger',
    });
    await toast.present();
  }

  private resetForm(): void {
    this.quickAddForm.reset({
      firstname: '',
      lastname: '',
      email: '',
      rolelead: false,
      rolefin: false,
    });
    this.duplicateEmailError.set(false);
    this.permissionsRequiredError.set(false);
    this.submitting.set(false);
  }

  private isDuplicateEmail(
    trip: NonNullable<ReturnType<typeof this.tripService.currentTrip.value>>,
    email: string,
  ): boolean {
    if (!hasTripLeadership(trip)) {
      return false;
    }

    const normalizedEmail = email.trim().toLowerCase();
    const onRoster = trip.roster.some(
      (participant) => participant.email?.trim().toLowerCase() === normalizedEmail,
    );
    const onNonRoster = trip.nonRosterUsers.some(
      (participant) => participant.email?.trim().toLowerCase() === normalizedEmail,
    );

    return onRoster || onNonRoster;
  }
}
